/**
 * src/nav/registry.js — Fuente única de la navegación del OS.
 *
 * Antes: la lista vivía inline en Layout.jsx (buildNavGroups + NAV_GROUPS legacy)
 * Y en CommandPalette.jsx duplicada con 17 rutas hardcoded.
 * Ahora: los dos consumen de acá.
 *
 * Exports:
 *   - buildNavGroups(nav, badges) → estructura completa role-aware con badges runtime
 *   - ROUTE_SCOPES → { ruta: "personal" | "cliente" | "agencia" }
 *   - matchesRole(userRolStr, allowedRoles[]) → bool
 *   - ROLE_ALIASES → mapping para match laxo
 *   - isClientScopedRoute(pathname) → bool
 *   - PAGE_TITLES → { ruta: título mostrable }
 *   - flattenNavItems(nav, badges) → lista plana para CommandPalette
 *
 * Este archivo NO importa React. Todos los íconos se importan de lucide-react
 * (usados como componentes en el renderer).
 */
import {
  LayoutDashboard, Kanban, List, CalendarDays, Clock,
  Users, UserSquare2,
  Settings, FolderOpen,
  Camera, Activity, DollarSign, Briefcase, Target, Home, Sparkles, Lightbulb,
  MessageSquare, Receipt, AlertCircle, History, FileText, BrainCircuit,
  Calculator, TrendingDown, TrendingUp, Shield, Inbox, Package, UserPlus, Megaphone, Film,
  Send, XCircle, Building2, Award, User, Layers, Trophy, Cpu, Wallet, GraduationCap,
} from "../lib/icons.jsx";

/* ══════════════════════════════════════════════════════════════════════════
 *  buildNavGroups(nav, badges) — estructura role-aware dinámica
 *
 *  `nav` viene de useRolNavigation()  → { miCockpit, miCockpitLabel, miBandeja, ... }
 *  `badges` viene de useOSBadges()    → { mi_bandeja, mis_tareas, subtareas_pendientes, ... }
 * ══════════════════════════════════════════════════════════════════════════ */
export function buildNavGroups(nav, badges) {
  return [
  /* ── HOY — Lo del día: tu cockpit, la cola de trabajo, lo que te escribieron y el chat ── */
  {
    label: "Hoy",
    items: [
      { to: nav.miCockpit, label: nav.miCockpitLabel, icon: Activity, scope: "personal" },
      { to: nav.miBandeja, label: nav.miBandejaLabel, icon: Inbox, scope: "personal",
        badge: badges.mi_bandeja > 0 ? badges.mi_bandeja : null, badgeColor: "purple" },
      { to: "/novedades", label: "Novedades", icon: Inbox, scope: "personal",
        hint: "Todo lo que te escribieron y lo que espera tu respuesta, en un solo lugar" },
      { to: "/chat", label: "Chat", icon: MessageSquare, scope: "personal",
        badge: badges.chat_no_leidos > 0 ? badges.chat_no_leidos : null, badgeColor: "purple",
        hint: "Mensajes directos, canales del equipo y el canal compartido de cada cliente" },
    ],
  },

  /* ── MI TRABAJO — Lo tuyo: tus tareas, tu tiempo, tu desempeño y tu aprendizaje ── */
  {
    label: "Mi trabajo",
    items: [
      { to: "/tareas?mis=1",    label: "Mis Tareas",      icon: List,          shortcut: "T", scope: "personal",
        badge: badges.mis_tareas > 0 ? badges.mis_tareas : null, badgeColor: "amber" },
      // Mi Día se renderiza suelto arriba en Layout — se saca del grupo (v8.4.0).
      { to: "/horas",           label: "Mi Tiempo",       icon: Clock,         shortcut: "H", scope: "personal" },
      { to: "/mi-trabajo",      label: "Mi trabajo",      icon: Briefcase,     scope: "personal",
        hint: "Lo que hiciste en el día, la semana y el mes, y lo que anotás fuera del sistema" },
      { to: "/feedback",        label: "Mi Feedback",     icon: MessageSquare, scope: "personal" },
      { to: "/scorecard-rol",   label: "Mi Scorecard",    icon: Award,         scope: "personal",
        nuevo: "v60-scorecard-rol",
        hint: "Métricas adaptadas a tu rol funcional" },
      { to: "/hoja-1a1",       label: "Mi hoja de 1:1",  icon: FileText,      scope: "personal",
        hint: "La hoja de tu conversación semanal: qué hiciste, cómo viene y qué te frenó" },
      { to: "/actividad-hoy",   label: "Mi Actividad Hoy", icon: Clock,        scope: "personal",
        hint: "Timeline de tu día: transiciones, comentarios, edits" },
      { to: "/mi-perfil",       label: "Mi perfil",       icon: User,          scope: "personal",
        hint: "Foto y datos personales · usados en Hall of Fame" },
      { to: "/mi-cartera",      label: "Mi cartera",      icon: Users,         scope: "personal",
        roles: ["EC"],
        hint: "Cómo está cada cliente y qué te toca hoy" },
      { to: "/mis-cuentas",     label: "Mis cuentas",     icon: Layers,        scope: "personal",
        roles: ["COM"],
        hint: "El plan del mes, la cola de revisión y los briefs de cada cuenta que llevás" },
      { to: "/pizarra",               label: "Pizarra",            icon: Lightbulb,  scope: "agencia",
        hint: "Ideas, bajadas, fechas y eventos de cada marca para tener presentes al armar planes y tareas" },
      { to: "/academia",        label: "Crew Up",         icon: Sparkles,      scope: "personal",
        hint: "Formación + círculos de feedback" },
      { to: "/procedimientos",  label: "Procedimientos",  icon: FileText,      scope: "personal",
        hint: "SOPs operativos · cómo se hacen las cosas" },
      // Academia SOA 10 PRO (detrás de ff_academia: Layout la saca del menú si el flag está apagado)
      { to: "/ayuda/tutoriales", label: "Tutoriales",      icon: GraduationCap, scope: "personal",
        hint: "Aprendé el SOA 10 PRO paso a paso, sobre la pantalla real" },
    ],
  },

  /* ── OPERACIÓN — El trabajo de las cuentas: tareas, agenda, coberturas, pedidos y publicación ── */
  {
    label: "Operación",
    items: [
      { to: "/tareas",              label: "Tareas",           icon: List,             scope: "cliente",
        hint: "Incluye Kanban, Calendario y Gantt como vistas" },
      { to: "/agenda",              label: "Agenda",           icon: CalendarDays,     scope: "cliente",
        hint: "Mes / Semana / Día · tareas + coberturas en una sola vista" },
      { to: "/feed-preview",        label: "Feed Preview",     icon: Layers,           scope: "cliente",
        roles: ["CEO", "COO", "EC", "CCC", "COM"],
        hint: "Vista grid del plan con material real · drag&drop · versionado · multi-canal" },
      { to: "/status-semana",       label: "Status Semanal",   icon: CalendarDays,     scope: "cliente",
        roles: ["CEO", "COO", "EC", "CCC", "COM"],
        hint: "Vista semanal kanban: publicados / por publicar / en proceso / bloqueados" },
      { to: "/publicacion",         label: "Publicación",      icon: Megaphone,        scope: "agencia",
        roles: ["CEO", "MD", "COO", "EC", "COM", "SMM"],
        hint: "Piezas que el cliente aprobó y esperan salir: copy, finales y registro del enlace al publicar" },
      { to: "/pedidos-cliente",     label: "Pedidos Cliente",  icon: Inbox,            scope: "personal",
        badge: badges.pedidos_cliente > 0 ? badges.pedidos_cliente : null, badgeColor: "amber",
        roles: ["CEO", "COO", "EC", "CCC", "COM"] },
      { to: "/pedidos-agencia",     label: "Pedidos a Cliente",icon: Send,             scope: "agencia",
        roles: ["CEO", "COO", "EC", "CCC", "COM"],
        badge: badges.pedidos_agencia > 0 ? badges.pedidos_agencia : null, badgeColor: "amber",
        hint: "Lo que la agencia le pide al cliente (aprobaciones, briefs, materiales)" },
      { to: "/portal-inbox",        label: "Chats Portal",     icon: MessageSquare,    scope: "personal",
        roles: ["CEO", "COO", "EC", "CCC", "COM"],
        hint: "Conversaciones 1-a-1 con clientes del portal" },
      { to: "/briefs-plan",         label: "Briefs del Plan",  icon: FileText,         scope: "agencia",
        roles: ["CEO", "COO", "EC", "CCC", "COM"] },
      { to: "/generador-contenido", label: "Generador IA",     icon: Sparkles,         scope: "cliente"  },
      { to: "/coberturas-v2",       label: "Coberturas",       icon: Camera,           scope: "cliente",
        roles: ["CEO","COO","CCC","CPC","EC","COM","Fotógrafo","Audiovisual"],
        hint: "Eventos con crew · brief estructurado + Ficha Técnica v2 + SLA crudos" },
      { to: "/coberturas/control", label: "Coberturas · control", icon: Cpu, scope: "agencia",
        roles: ["CEO", "MD", "COO", "CPC"], hint: "Disponibilidad del crew, postproducción e indicadores" },
      { to: "/campo",            label: "Hoy en campo",      icon: Cpu,         scope: "agencia",
        roles: ["CEO", "MD", "COO", "CPC", "Audiovisual", "Fotógrafo", "Diseñador", "EC", "COM"], hint: "Coberturas de hoy: lugar, contacto, lista de salida, Llegué / Terminé" },
      { to: "/live-events",         label: "Live Events / BTL",icon: Sparkles,         scope: "agencia",
        roles: ["CEO", "COO", "EC", "CCC", "COM", "CPC"] },
      { to: "/producciones-terceros", label: "Producciones",       icon: Film,       scope: "agencia",
        roles: ["CEO", "COO", "CCC", "CPC", "EC"],
        hint: "Servicios a terceros · agencia como productora B2B" },
    ],
  },

  /* ── CUENTAS — Cada cliente: cómo está, qué se le entrega y qué falta cargar ── */
  {
    label: "Cuentas",
    items: [
      { to: "/clientes",              label: "Clientes",           icon: Users,      scope: "agencia",
        hint: "Mis Clientes y Todos como tabs" },
      { to: "/cartera",               label: "Salud de la cartera", icon: TrendingUp, scope: "agencia",
        roles: ["CEO", "MD", "COO", "CFO", "CCC", "EC", "COM"], hint: "Cómo está cada cuenta (entrega, calidad, relación, riesgo y marca), con una sola definición de salud" },
      { to: "/reportes",              label: "Reportes",           icon: FileText,   scope: "agencia",
        roles: ["CEO", "MD", "COO", "CFO", "EC", "COM", "Gerente"], hint: "El informe de trabajos realizados de cada marca y el archivo de reportes" },
      { to: "/reportes-clientes",     label: "Reportes de clientes", icon: FileText, scope: "agencia",
        roles: ["CEO", "MD", "COO", "CCC", "EC", "COM"], hint: "El reporte mensual que ve cada cliente: se arma con lo medido, lo revisás y lo publicás" },
      // v8.6.23 · Fase 51 — pedido explícito: "deben poder ver los 3 [MD/
      // COO/EC] — la función total de nuestro sistema depende de que estén
      // completos estos datos."
      { to: "/banco-recursos",        label: "Banco de Recursos",  icon: BrainCircuit, scope: "agencia",
        roles: ["CEO", "MD", "COO", "EC", "COM"],
        hint: "% de completitud por cliente — qué falta cargar esta semana" },
      { to: "/espacios",              label: "Espacios",           icon: Layers,     scope: "agencia",
        hint: "Proyectos, transiciones, eventos y campañas con resultado, gente, plan y medición" },
      { to: "/calidad/cliente",  label: "Calidad con el cliente", icon: TrendingUp, scope: "agencia",
        roles: ["CEO", "MD", "COO", "COM"], hint: "Qué % de las piezas enviadas el cliente devuelve con cambios, por cuenta y por mes (el COM ve las suyas)" },
      { to: "/leads",                 label: "Pipeline Leads",     icon: Target,     scope: "agencia",
        roles: ["CEO", "COO", "Comercial", "Hunter", "Farmer"] },
      { to: "/partners",              label: "Partners & Influencers", icon: UserPlus, scope: "agencia",
        roles: ["CEO", "COO", "EC", "CCC", "CPC", "COM"] },
      { to: "/proveedores",           label: "Proveedores rankeados", icon: Award, scope: "agencia",
        roles: ["CEO", "COO", "EC", "CCC", "CPC", "Gerente"],
        hint: "Ranking interno · editores, fotógrafos, productoras · evaluaciones" },
      { to: "/sorteos",               label: "Sorteos",            icon: Trophy,     scope: "agencia",
        roles: ["CEO","COO","CCC","COM","EC"],
        hint: "Sorteos en vivo · base de cupones · multi-premio · display público" },
      { to: "/cuentas/salud",    label: "Salud de cuentas",  icon: Cpu,         scope: "agencia",
        roles: ["CEO", "MD", "COO"], hint: "Qué cuentas tienen el equipo completo y cuáles no" },
      { to: "/risk-radar",            label: "Risk Radar",         icon: AlertCircle,scope: "agencia",
        roles: ["CEO", "COO", "EC", "CFO", "Gerente"] },
    ],
  },

  /* ── EQUIPO — Las personas: carga, desempeño y reparto del trabajo ── */
  {
    label: "Equipo",
    items: [
      { to: "/equipo",            label: "Equipo",             icon: UserSquare2,  scope: "agencia" },
      { to: "/equipo/dashboard",      label: "Desempeño equipo",   icon: TrendingUp, scope: "agencia",
        roles: ["CEO", "COO", "CFO", "CCC", "Gerente"] },
      { to: "/trabajo-equipo",      label: "Trabajo del equipo", icon: TrendingUp, scope: "agencia",
        roles: ["CEO", "MD", "COO", "CCC"],
        hint: "Qué hizo cada rol y cada persona, y cómo viene la cartera de cada EC" },
      { to: "/analytics/equipo",      label: "Analytics Equipo",   icon: TrendingUp, scope: "agencia",
        roles: ["CEO", "COO", "CCC"],
        hint: "9 métricas: re-trabajo, rebote COM, FTR, cycle time, cobertura, tareas estancadas" },
      { to: "/especialistas-dashboard", label: "Especialistas",    icon: Users,      scope: "agencia",
        roles: ["CEO", "COO", "CFO", "CCC", "CPC", "Gerente"],
        hint: "Carga de trabajo + estado de tareas/coberturas por especialista" },
      { to: "/carga-forecast",        label: "Carga Forecast",     icon: TrendingUp, scope: "agencia",
        roles: ["CEO", "COO", "CFO", "CCC", "Gerente"],
        hint: "Heatmap día×persona próximos 7-14 días + recomendaciones" },
      { to: "/equipo/cierre-dia",     label: "Cierre del día",     icon: Sparkles,   scope: "agencia",
        roles: ["CEO", "MD", "COO", "CCC"],   // MD tiene el mismo nivel que CEO: antes no veía esta entrada
        hint: "Estado del Mi Día de cada miembro · filtrable por rol" },
      { to: "/departamento/DEP-AUDIOVISUAL", label: "Departamentos", icon: Cpu, scope: "agencia",
        roles: ["CEO", "MD", "COO"], hint: "Carga y pronóstico de Audiovisual, Fotografía y Diseño" },
      { to: "/asignacion",        label: "Asignación IA",      icon: Sparkles,     scope: "agencia",
        roles: ["CEO", "COO", "CCC"], hint: "Distribuir tareas y ver carga del equipo creativo" },
      { to: "/asignador",        label: "Asignador",         icon: Cpu,         scope: "agencia",
        roles: ["CEO", "MD", "COO"], hint: "Modo sombra: cuánto coincide con lo que asignan las personas" },
      { to: "/apelaciones",       label: "Apelaciones",        icon: AlertCircle,  scope: "agencia",
        roles: ["CCC", "CEO", "COO", "EC"] },
      { to: "/equipos",           label: "Equipos & Alquiler", icon: Package,      scope: "agencia",
        roles: ["CEO", "COO", "CPC", "Gerente"] },
      { to: "/rrhh",              label: "RRHH",               icon: Users,        scope: "agencia",
        roles: ["CEO", "COO", "CFO"] },
      { to: "/gamificacion/podio",    label: "Podio · Hall of Fame", icon: Award,    scope: "agencia",
        hint: "Ranking global + Liga de los 4 (Velocidad/Calidad/Colaboración/Constancia)" },
      { to: "/analytics/canceladas",  label: "Tareas Canceladas",  icon: XCircle,    scope: "agencia",
        roles: ["CEO", "COO", "CCC", "EC", "Gerente"], hint: "Qué se cancela, por cliente / mes / responsable" },
    ],
  },

  /* ── DIRECCIÓN Y FINANZAS — Resultados del negocio: cockpits, P&L, facturación y costos ── */
  {
    label: "Dirección y finanzas",
    items: [
      { to: "/cockpit",               label: "Cockpits",           icon: Activity,   scope: "agencia",
        roles: ["CEO", "MD", "COO", "CFO", "CCC"], nuevo: "v830-cockpit-unificado",
        hint: "Hub unificado: CEO / COO / Financiero / Comercial · selector interno" },
      { to: "/okrs",                  label: "OKRs",               icon: Target,     scope: "agencia",
        roles: ["CEO", "COO", "CFO", "Comercial", "Gerente"] },
      { to: "/dashboard",             label: "Dashboard P&L",      icon: LayoutDashboard, shortcut: "D", scope: "agencia",
        roles: ["CEO", "COO", "CFO"] },
      { to: "/proyectos",             label: "Rentabilidad Proyectos", icon: FolderOpen, scope: "cliente",
        roles: ["CEO", "COO", "CFO", "EC", "CCC", "Gerente"],
        hint: "P&L por proyecto + importar plan + compilar deck" },
      { to: "/agency-os",         label: "Agency OS · AGI + BUs", icon: DollarSign,   scope: "agencia",
        roles: ["CEO", "COO", "CFO"] },
      // v8.6.22 · Fase 8 (Tier 1)
      { to: "/cockpit/md",       label: "P&L por Unidad",    icon: Building2,   scope: "agencia",
        roles: ["CEO", "MD", "COO", "CFO"], hint: "P&L y márgenes por Unidad de Negocio" },
      { to: "/admin/financiero",  label: "Admin Financiero",      icon: Briefcase,    scope: "agencia",
        roles: ["CEO", "COO", "CFO"] },
      { to: "/fee-ledger",        label: "Fee Ledger",            icon: DollarSign,   scope: "agencia",
        roles: ["CEO", "COO", "CFO", "CCC", "EC"], nuevo: "v830-fee-ledger@2026-07-19",
        hint: "Matriz de créditos por cliente · excesos pendientes · pausa por mora" },
      { to: "/facturacion",       label: "Facturación",           icon: Receipt,      scope: "agencia",
        roles: ["CEO", "COO", "CFO"] },
      { to: "/admin/cobranzas",   label: "Cobranzas",             icon: AlertCircle,  scope: "agencia",
        roles: ["CEO", "COO", "CFO"],
        hint: "Aging por bucket + recordatorios automáticos" },
      { to: "/media-ops",         label: "Media",                 icon: Megaphone,    scope: "agencia",
        roles: ["CEO", "COO", "CFO"],
        hint: "Operations + Buying SaaS unificados" },
      { to: "/costos-operativos", label: "Costos Operativos",     icon: TrendingDown, scope: "agencia",
        roles: ["CEO", "COO", "CFO"] },
      { to: "/costeo",            label: "Costeo Cliente",        icon: Calculator,   scope: "agencia",
        roles: ["CEO", "COO", "CFO"] },
      { to: "/viaticos",         label: "Viáticos",          icon: Wallet,      scope: "agencia",
        roles: ["CEO", "MD", "COO", "CFO"], hint: "Cola de aprobación de viáticos de coberturas" },
    ],
  },

  /* ── AGENTES Y SISTEMA — Lo que trabaja solo: agentes, casos, autopiloto y sus mediciones ── */
  {
    label: "Agentes y sistema",
    items: [
      { to: "/agentes",          label: "Centro de agentes", icon: Cpu,         scope: "agencia",
        roles: ["CEO", "MD", "COO", "CFO", "IT"], hint: "Quién trabaja solo, de quién responde cada agente y el botón de pausa" },
      { to: "/it-lead",          label: "Salud de Agentes",  icon: Cpu,         scope: "agencia",
        roles: ["CEO", "MD", "COO", "CFO", "IT"], hint: "Crons/agentes: última corrida, éxito, duración" },
      { to: "/casos",               label: "Casos",            icon: AlertCircle,      scope: "agencia",
        roles: ["CEO", "MD", "COO", "EC", "COM", "SMM", "CPC"],
        hint: "Problemas detectados por los agentes, cada uno con su dueño y su causa" },
      { to: "/autopiloto",       label: "Autopiloto",        icon: Cpu,         scope: "agencia",
        roles: ["CEO", "MD", "COO"], hint: "Recorrido de una cuenta: plan, tareas, piezas, calidad y lo que ve el cliente" },
      { to: "/autopiloto/perfiles", label: "Perfiles de autonomía", icon: Cpu, scope: "agencia",
        roles: ["CEO", "MD", "COO"], hint: "Qué cuentas trabajan con el agente y en qué nivel; qué le falta a cada una para subir" },
      { to: "/autopiloto/corridas", label: "Corridas del Autopiloto", icon: Cpu, scope: "agencia",
        roles: ["CEO", "MD", "COO"], hint: "El mes de cada cuenta recorriendo el flujo: plan, tareas, piezas y calidad, hasta la bandeja del EC" },
      { to: "/transicion",       label: "Tablero de transición", icon: TrendingUp, scope: "agencia",
        roles: ["CEO", "MD", "COO", "EC"], hint: "Cómo está cada cuenta, qué le falta y si está lista para trabajar con los bots" },
      { to: "/tiempos",          label: "Tiempos",           icon: Clock,       scope: "agencia",
        roles: ["CEO", "MD", "COO"], hint: "Cuánto tarda cada etapa y en manos de qué rol, la cola de revisión y el esfuerzo por tipo de pieza" },
      { to: "/conversaciones",   label: "Conversaciones",    icon: MessageSquare, scope: "agencia",
        roles: ["CEO", "MD", "COO", "EC"], hint: "Chats de WhatsApp de cada cuenta, manual de atención y reuniones, con búsqueda" },
      { to: "/bandeja-cuentas",   label: "Bandeja de cuentas", icon: Inbox, scope: "agencia",
        roles: ["CEO", "MD", "COO", "EC"], hint: "Qué necesita hoy cada cuenta del asistente, con borradores de mensaje para revisar" },
    ],
  },

  /* ── ADMINISTRACIÓN — Accesos, datos de la agencia y auditoría ────── */
  {
    label: "Administración",
    items: [
      { to: "/admin/roles",      label: "Roles & Permisos",  icon: Shield,      scope: "agencia",
        roles: ["CEO", "COO"] },
      { to: "/admin/usuarios",   label: "Usuarios sistema",  icon: UserSquare2, scope: "agencia",
        roles: ["CEO", "COO"] },
      { to: "/usuarios-evento", label: "Usuarios por evento", icon: Cpu, scope: "agencia",
        roles: ["CEO", "MD", "COO", "CPC", "IT"], hint: "Cuentas temporales con vigencia y alcance para gente de afuera" },
      { to: "/admin/bulk",       label: "Edición masiva",    icon: FileText,    scope: "agencia",
        roles: ["CEO", "COO"], hint: "Bajar/subir XLSX de equipo + clientes" },
      { to: "/admin/agencia",    label: "Datos Agencia",     icon: Building2,   scope: "agencia",
        roles: ["CEO", "COO", "CFO"], hint: "RUC, timbrado, cuentas, tarjetas, pólizas, servicios" },
      { to: "/admin/drive-health", label: "Drive Health",    icon: FolderOpen,    scope: "agencia",
        roles: ["CEO", "COO", "CFO"],
        hint: "Detecta y repara clientes sin carpeta madre en Drive" },
      { to: "/admin/settings",   label: "Settings",          icon: Settings,    scope: "agencia",
        roles: ["CEO", "MD", "COO", "IT"],
        hint: "Notificaciones · Bot Chat · Setup pendiente · preferencias" },
      { to: "/ai-cost",          label: "AI Cost Cockpit",   icon: BrainCircuit,scope: "agencia",
        roles: ["CEO", "MD", "COO", "CFO", "IT"] },
      { to: "/audit",            label: "Audit Trail",       icon: History,     scope: "agencia",
        roles: ["CEO", "MD", "COO", "CFO", "IT"] },
    ],
  },
  ];
}

/* ══════════════════════════════════════════════════════════════════════════
 *  ROUTE_SCOPES — { ruta: "personal" | "cliente" | "agencia" }
 *  Único uso: saber si el selector de cliente aplica a la página actual.
 *  Mantener sincronizado a mano con buildNavGroups() de arriba — no se puede
 *  derivar automáticamente porque buildNavGroups() necesita contexto de rol
 *  en runtime (nav.miCockpit, nav.miBandeja son dinámicos por rol).
 *  (Reemplaza al NAV_GROUPS_STATIC legacy que había quedado desincronizado
 *  con nombres de grupo y rutas viejas — ver auditoría UX 2026-08-05.)
 * ══════════════════════════════════════════════════════════════════════════ */
export const ROUTE_SCOPES = {
  "/inicio": "personal",
  "/tareas?mis=1": "personal",
  "/horas": "personal",
  "/feedback": "personal",
  "/scorecard-rol": "personal",
  "/actividad-hoy": "personal",
  "/mi-perfil": "personal",
  "/academia": "personal",
  "/procedimientos": "personal",
  "/ayuda/tutoriales": "personal",

  "/tareas": "cliente",
  "/agenda": "cliente",
  "/feed-preview": "cliente",
  "/status-semana": "cliente",
  "/pedidos-cliente": "personal",
  "/portal-inbox": "personal",
  "/pedidos-agencia": "agencia",
  "/briefs-plan": "agencia",
  "/generador-contenido": "cliente",
  "/coberturas-v2": "cliente",
  "/live-events": "agencia",

  "/clientes": "agencia",
  "/espacios": "agencia",
  "/leads": "agencia",
  "/partners": "agencia",
  "/proveedores": "agencia",
  "/producciones-terceros": "agencia",
  "/risk-radar": "agencia",

  "/cockpit": "agencia",
  "/reportes": "agencia",
  "/dashboard": "agencia",
  "/proyectos": "cliente",
  "/okrs": "agencia",
  "/equipo/dashboard": "agencia",
  "/gamificacion/podio": "agencia",
  "/equipo/cierre-dia": "agencia",
  "/equipo/cierre-semana": "agencia",
  "/mi-trabajo": "personal",
  "/mi-cartera": "personal",
  "/mis-cuentas": "personal",
  "/hoja-1a1": "personal",
  "/trabajo-equipo": "agencia",
  "/analytics/equipo": "agencia",
  "/sorteos": "agencia",
  "/carga-forecast": "agencia",
  "/especialistas-dashboard": "agencia",
  "/analytics/canceladas": "agencia",

  "/fee-ledger": "agencia",
  "/agency-os": "agencia",
  "/admin/financiero": "agencia",
  "/facturacion": "agencia",
  "/admin/cobranzas": "agencia",
  "/media-ops": "agencia",
  "/costos-operativos": "agencia",
  "/costeo": "agencia",

  "/equipo": "agencia",
  "/asignacion": "agencia",
  "/apelaciones": "agencia",
  "/equipos": "agencia",
  "/rrhh": "agencia",

  "/admin/roles": "agencia",
  "/admin/usuarios": "agencia",
  "/admin/bulk": "agencia",
  "/admin/agencia": "agencia",
  "/admin/drive-health": "agencia",
  "/admin/settings": "agencia",
  "/ai-cost": "agencia",
  "/audit": "agencia",
  "/cockpit/md": "agencia",
  "/it-lead": "agencia",
  "/asignador": "agencia",
  "/departamento/DEP-AUDIOVISUAL": "agencia",
  "/transicion": "agencia",
  "/cartera": "agencia",
  "/reportes-clientes": "agencia",
  "/pizarra": "agencia",
  "/conversaciones": "agencia",
  "/chat": "personal",
  "/novedades": "personal",
  "/bandeja-cuentas": "agencia",
  "/autopiloto": "agencia",
  "/calidad/cliente": "agencia",
  "/tiempos": "agencia",
  "/publicacion": "agencia",
  "/agentes": "agencia",
  "/casos": "agencia",
  "/autopiloto/perfiles": "agencia",
  "/autopiloto/corridas": "agencia",
  "/cuentas/salud": "agencia",
  "/campo": "agencia",
  "/usuarios-evento": "agencia",
  "/coberturas/control": "agencia",
  "/departamento": "agencia",
  "/viaticos": "agencia",
  "/banco-recursos": "agencia",
};

export const ROLE_ALIASES = {
  // v8.6.22 · Fase 7 — MD (mismo nivel que CEO): agregar "md" acá hace que
  // CUALQUIER item de nav que ya requiera "CEO" también matchee a un
  // usuario con rol="MD", sin tocar los ~15+ arrays de roles individuales.
  ceo:       ["director creativo", "director general", "fundador", "founder", "owner", "md"],
  coo:       ["operaciones", "arquitecta de procesos", "arquitecto de procesos"],
  cfo:       ["financiera", "financiero", "finanzas"],
  ec:        ["ejecutiv", "cuenta", "estratega de crecimiento", "estratega"],
  com:       ["content manager", "smm", "community", "cm "],
  ccc:       ["control de calidad", "centro de control"],
  cpc:       ["coordinador de producci", "producción", "produccion"],
  comercial: ["hunter", "ventas", "gestor comercial"],
  gerente:   ["gerencia", "jefe"],
};

export function matchesRole(userRol, allowedRoles) {
  if (!userRol || !allowedRoles?.length) return false;
  const r = userRol.toLowerCase();
  return allowedRoles.some(allowed => {
    const a = allowed.toLowerCase();
    if (r === a || r.includes(a)) return true;
    const aliases = ROLE_ALIASES[a] || [];
    return aliases.some(alias => r.includes(alias));
  });
}

export function isClientScopedRoute(pathname) {
  if (pathname.startsWith("/clientes/")) return true;
  return ROUTE_SCOPES[pathname] === "cliente";
}

/* ══════════════════════════════════════════════════════════════════════════
 *  Títulos mostrables por ruta (header). Estático — no depende de contexto.
 * ══════════════════════════════════════════════════════════════════════════ */
export const PAGE_TITLES = {
  // Mi Espacio
  "/inicio":          "Mi Día",
  "/tareas?mis=1":    "Mis Tareas",
  "/horas":           "Mi Tiempo",
  "/feedback":        "Feedback",
  "/scorecard-rol":   "Mi Scorecard",
  "/actividad-hoy":   "Actividad de Hoy",
  "/mi-perfil":       "Mi Perfil",
  "/academia":        "Crew Up",
  "/procedimientos":  "Procedimientos",
  "/ayuda/tutoriales": "Tutoriales",
  "/client-feedback": "Feedback de Clientes",
  // Operación
  "/tareas":              "Tareas",
  "/kanban":              "Kanban",
  "/gantt":               "Gantt",
  "/calendario":          "Calendario",
  "/agenda":              "Agenda",
  "/feed-preview":        "Feed Preview",
  "/status-semana":       "Status Semanal",
  "/pedidos-cliente":     "Pedidos Cliente",
  "/portal-inbox":        "Chats Portal",
  "/pedidos-agencia":     "Pedidos a Cliente",
  "/briefs-plan":         "Briefs del Plan",
  "/generador-contenido": "Generador de Contenido IA",
  "/coberturas-v2":       "Coberturas",
  "/coberturas-v2/nueva": "Nueva Cobertura",
  "/live-events":         "Live Events / BTL",
  "/buscar-producciones": "Buscar Producciones",
  "/media-buying":        "Media Buying",
  "/circles":             "Circles",
  "/moodboards-test":     "Moodboards Test",
  "/inbox-hub":           "Inbox Hub",
  "/ccc/inbox":           "Bandeja CCC",
  "/com/inbox":           "Bandeja COM",
  "/ec/inbox":            "Bandeja EC",
  "/settings/notificaciones": "Notificaciones",
  // Cartera
  "/clientes":              "Clientes",
  "/espacios":              "Espacios",
  "/mis-clientes":          "Mis Clientes",
  "/leads":                 "Pipeline de Leads",
  "/partners":              "Partners",
  "/influencers":           "Influencers",
  "/proveedores":           "Proveedores",
  "/producciones-terceros": "Producciones de Terceros",
  "/risk-radar":            "Risk Radar",
  // Análisis
  "/cockpit":                 "Cockpit",
  "/cockpit/coo":             "Cockpit COO",
  "/cockpit/ceo":             "Cockpit CEO",
  "/cockpit/coo-v2":          "Cockpit COO v2",
  "/cockpit/ceo-v2":          "Cockpit CEO v2",
  "/ccc/cockpit":             "Cockpit CCC",
  "/com/cockpit":             "Cockpit COM",
  "/ec/cockpit":              "Cockpit EC",
  "/cpc/cockpit":             "Cockpit CPC",
  "/otros-cockpits":          "Otros Cockpits",
  "/reportes":                "Reportes",
  "/reportes/suscripciones":  "Reportes de Suscripciones",
  "/dashboard":               "Dashboard",
  "/proyectos":               "Rentabilidad de Proyectos",
  "/okrs":                    "OKRs",
  "/equipo/dashboard":        "Desempeño de Equipo",
  "/gamificacion/podio":      "Podio · Hall of Fame",
  "/equipo/cierre-dia":       "Cierre del Día",
  "/equipo/cierre-semana":    "Cierre de la Semana",
  "/mi-trabajo":              "Mi trabajo",
  "/mi-cartera":              "Mi cartera",
  "/mis-cuentas":             "Mis cuentas",
  "/hoja-1a1":                "Hoja de 1:1",
  "/trabajo-equipo":          "Trabajo del equipo",
  "/analytics/equipo":        "Analytics de Equipo",
  "/analytics/subtareas":     "Analytics de Subtareas",
  "/sorteos":                 "Sorteos",
  "/carga-forecast":          "Carga Forecast",
  "/especialistas-dashboard": "Especialistas",
  "/analytics/canceladas":    "Tareas Canceladas",
  "/mi-pipeline":             "Mi Pipeline",
  // Negocio
  "/fee-ledger":         "Fee Ledger",
  "/agency-os":          "Agency OS · AGI + BUs",
  "/admin/financiero":   "Administrativo Financiero",
  "/facturacion":        "Facturación",
  "/admin/cobranzas":    "Cobranzas",
  "/media-ops":          "Media Operations",
  "/costos-operativos":  "Costos Operativos",
  "/costeo":             "Costeo · Cost-per-hour",
  "/finanzas":           "Cockpit Financiero",
  // Equipo
  "/equipo":     "Equipo",
  "/asignacion": "Asignación IA",
  "/apelaciones":"Apelaciones",
  "/equipos":    "Equipos & Alquiler",
  "/rrhh":       "RRHH",
  "/admin/cumplimiento-org": "Cumplimiento Organizacional",
  // Admin
  "/admin/roles":         "Roles & Permisos",
  "/admin/usuarios":      "Usuarios del Sistema",
  "/admin/bulk":          "Edición Masiva",
  "/admin/agencia":       "Datos Agencia",
  "/admin/drive-health":  "Drive Health",
  "/admin/settings":      "Configuración",
  "/admin/chat-status":   "Estado del Bot Chat",
  "/admin/import-cliente":"Importar Cliente",
  "/agent-inbox":         "Agent Inbox",
  "/setup-pendientes":    "Setup Pendiente",
  "/recetas":             "Recetas",
  "/ai-cost":             "AI Cost Cockpit",
  "/audit":               "Audit Trail",
  "/cockpit/md":          "Cockpit MD · P&L por Unidad",
  "/it-lead":             "IT Lead · Salud de Agentes",
  "/asignador":           "Asignador",
  "/departamento/DEP-AUDIOVISUAL": "Departamentos",
  "/transicion":          "Tablero de transición",
  "/cartera":             "Salud de la cartera",
  "/reportes-clientes":   "Reportes de clientes",
  "/pizarra":             "Pizarra",
  "/conversaciones":      "Conversaciones",
  "/chat":                "Chat",
  "/novedades":           "Novedades",
  "/bandeja-cuentas":     "Bandeja de cuentas",
  "/autopiloto":          "Autopiloto · recorrido de la cuenta",
  "/calidad/cliente":     "Calidad con el cliente",
  "/tiempos":             "Tiempos",
  "/publicacion":         "Publicación",
  "/agentes":             "Centro de agentes",
  "/casos":               "Casos",
  "/autopiloto/perfiles": "Perfiles de autonomía",
  "/autopiloto/corridas": "Corridas del Autopiloto",
  "/cuentas/salud":       "Salud de cuentas",
  "/campo":               "Hoy en campo",
  "/usuarios-evento":     "Usuarios por evento",
  "/coberturas/control":  "Coberturas · control",
  "/departamento":        "Departamento",
  "/viaticos":            "Viáticos · Aprobación",
  "/banco-recursos":      "Banco de Recursos · Completitud",
};

/* ══════════════════════════════════════════════════════════════════════════
 *  getDefaultFavorites(flags) — favoritos semilla por rol.
 *  Se usan solo la primera vez que un usuario abre el OS (favoritos vacíos +
 *  nunca sembrados) — a partir de ahí el usuario controla su propia lista,
 *  incluso si la deja en cero. `flags` viene de useAuth() (isAdmin, isCOO, etc).
 * ══════════════════════════════════════════════════════════════════════════ */
export function getDefaultFavorites(flags = {}) {
  const { isAdmin, isCOO, isCFO, isCCC, isCuenta, isCM, isEspecialista } = flags;
  if (isAdmin || isCOO || isCFO) {
    return ["/inicio", "/cockpit", "/clientes", "/tareas", "/fee-ledger"];
  }
  if (isCCC) {
    return ["/inicio", "/tareas", "/coberturas-v2", "/portal-inbox", "/clientes"];
  }
  if (isCuenta || isCM) {
    return ["/inicio", "/tareas", "/agenda", "/feed-preview"];
  }
  if (isEspecialista) {
    return ["/inicio", "/tareas", "/mi-pipeline"];
  }
  return ["/inicio", "/tareas", "/agenda"];
}

/* ══════════════════════════════════════════════════════════════════════════
 *  flattenNavItems(nav, badges) — lista plana de todos los items de nav.
 *  Uso: CommandPalette / búsqueda / audits.
 *
 *  Cada item conserva su `group` (label del grupo padre) para categorización.
 * ══════════════════════════════════════════════════════════════════════════ */
export function flattenNavItems(nav, badges) {
  const groups = buildNavGroups(nav, badges);
  const out = [];
  // "Mi Día" suelto (no está en NAV_GROUPS_DYN, se renderiza aparte en el Layout)
  out.push({ to: "/inicio", label: "Mi Día", icon: Home, group: "Inicio", scope: "personal" });
  for (const g of groups) {
    for (const it of g.items) {
      if (!it.to) continue;
      out.push({ ...it, group: g.label });
    }
  }
  return out;
}
